import React, {useState} from 'react'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import OutsideClickHandler from 'react-outside-click-handler'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import {LanguageType} from '@daedalus/core/src/localization/types/LanguageType'
import {hexToRGBStr} from '@daedalus/core/src/utils/string'
import FlagIcon from '../../../../designSystem/FlagIcon'
import {
  TriggerElement,
  PopupWrapper,
  ListHeader,
  ListElement,
  ListLinkElement,
  ListItemElement,
  NameElement
} from '../commonStyles'

const flagIconStyles = ({theme}) =>
  css`
    margin-right: ${theme.layout.spacing.s400}px;
    box-shadow: 0 1px 2px 0
      rgba(${hexToRGBStr(theme.colors.preferencePopupFlagShadow)}, 0.3);
    height: 11px;
  `

const FlagIconWrapper = styled('div')(flagIconStyles)

type Props = {
  /** List of languages to be displayed */
  availableLanguages: LanguageType[]
  /** The currently selected value */
  currentLanguage: string
  /** The callback for when a currency is selected. The new currency code will be passed. */
  onChangeLanguage: (code: string) => void
  /** The callback for when the button is clicked */
  onClick?: () => void
}

interface List extends Pick<Props, 'availableLanguages' | 'currentLanguage'> {
  handleOnChangeLanguage: (code: string) => React.MouseEventHandler
}

const List = ({
  availableLanguages,
  handleOnChangeLanguage,
  currentLanguage
}: List) => {
  return (
    <ListElement>
      {availableLanguages.map(language => (
        <ListItemElement key={language.code}>
          <ListLinkElement
            href="#"
            dataId="SelectLanguageHeading"
            isActive={currentLanguage === language.code}
            onClick={handleOnChangeLanguage(language.code)}
          >
            <FlagIconWrapper>
              <FlagIcon countryCode={language.code} />
            </FlagIconWrapper>
            <NameElement>{language.displayName}</NameElement>
          </ListLinkElement>
        </ListItemElement>
      ))}
    </ListElement>
  )
}

const LanguageSelector = ({
  currentLanguage,
  availableLanguages,
  onChangeLanguage,
  onClick
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOnChangeLanguage =
    (language: string) => (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      setIsOpen(false)

      onChangeLanguage(language)
    }

  const setClosed = () => setIsOpen(false)

  const handleClick = () => {
    if (onClick) onClick()
    setIsOpen(true)
  }

  return (
    <TriggerElement data-id="LanguageSelectorTrigger" onClick={handleClick}>
      <FlagIcon countryCode={currentLanguage} />

      {isOpen && (
        <OutsideClickHandler onOutsideClick={setClosed}>
          <PopupWrapper>
            <ListHeader>
              <FormattedMessageWrapper
                id="selectLanguage"
                defaultMessage="Select language"
              />
            </ListHeader>
            <List
              availableLanguages={availableLanguages}
              currentLanguage={currentLanguage}
              handleOnChangeLanguage={handleOnChangeLanguage}
            />
          </PopupWrapper>
        </OutsideClickHandler>
      )}
    </TriggerElement>
  )
}

export default LanguageSelector
